import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import Select from 'components/generic/form/Select'
import Checkbox from 'components/generic/form/Checkbox'
import { Typography } from '@mui/material'
import BasicDataGridV2 from 'components/generic/datagrid'
import ConfirmDialog from 'components/generic/modals/confirm-dialog/ConfirmDialog'
import Tooltip from 'components/generic/tooltip'
import { useArchiveDriverMutation } from 'services/drivers'
import { objectDetailsToDropdownFormatter } from 'utils/data-manipulation/common'
import { IconButton, Stack } from '@mui/material_modern'
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  PhonelinkRing as PhoneLinkRingIcon,
} from '@mui/icons-material_modern'
import { styled } from '@mui/system'
import { dispatchSnackbar } from '../../../../utils/helpers.tsx'
import { useGetGsUserListQuery } from 'services/gsUsers'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'

const GridStyling = styled('div')(({ theme }) => ({
  '& .MuiDataGrid-root': {
    border: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    padding: theme.spacing(1),
  },
  '& .MuiDataGrid-columnHeader': {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(0),
  },
  '& .MuiDataGrid-cell': {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  '& .MuiDataGrid-columnSeparator': {
    visibility: 'hidden',
  },
  // replace default hover / active-row styling from datagrid
  '& .MuiDataGrid-row': {
    '&.differential-row': {
      background: theme.palette.error.background,
      boxShadow: 'inset 0px -1px 0px #E0E0E0',
      '&:hover': {
        cursor: 'pointer',
        background: theme.palette.error.hover,
      },
    },
    '&.scrap-vehicle-row': {
      cursor: 'pointer',
    },
    '&.total-row': {
      backgroundColor: theme.palette.info.lightBg,
      '&:hover': {
        backgroundColor: theme.palette.info.lightBg,
      },
    },
    '&:not(.total-row)&:not(.differential-row)': {
      '&.Mui-selected': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
      '&:not(.Mui-selected)': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell.button-cell': {
    paddingLeft: 0,
    paddingRight: 0,
  },
  '& .MuiDataGrid-footerContainer': {
    display: 'none !important',
  },
}))

const ManageDriversProperties = ({ methods, driversData, trucksData, trailersData, isLoading }) => {
  const { control, setValue } = methods
  const dispatch = useDispatch()

  const [archiveDriver] = useArchiveDriverMutation()
  const { data: gsUsersResponse } = useGetGsUserListQuery({
    filter: JSON.stringify({
      search: '',
      userStatuses: ['ACTIVE'],
      sort: { field: 'name', ascending: true },
    }),
  })

  const [confirmArchiveModal, setConfirmArchiveModal] = useState(false)
  const [driverIdToArchive, setDriverIdToArchive] = useState('')

  const { enableEditDriverGsUser } = useAppSelector(state => state.featureFlags?.variables || {})

  const onArchiveDriver = async (driverId: string) => {
    await archiveDriver({ driverId })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'success', 'Successfully archived driver.')
      })
      .catch(() => {
        dispatchSnackbar(dispatch, 'error', 'Failed to archive driver')
      })
      .finally(() => {
        setConfirmArchiveModal(false)
      })
  }

  const driversColumns = useMemo(() => {
    const formattedTrucksData = trucksData?.map((truck, index) =>
      objectDetailsToDropdownFormatter(truck, index, { nameKey: 'assetName' }),
    )

    const formattedTrailersData = trailersData?.map((trailer, index) =>
      objectDetailsToDropdownFormatter(trailer, index, { nameKey: 'assetName' }),
    )

    const columns = [
      {
        field: 'isActive',
        headerName: 'Active',
        type: 'boolean',
        headerAlign: 'center',
        align: 'center',
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Checkbox
              name={`drivers.${row.index}.isActive`}
              label=""
              defaultValue={row.isActive}
              control={control}
              onChange={() => setValue(`drivers.${row.index}.edited`, true)}
            />
          )
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">{row.name}</Typography>
              {row.gsUserEmail && (
                <Tooltip title={row.gsUserEmail} placement="top">
                  <PhoneLinkRingIcon sx={{ marginLeft: '5px', fontSize: '1rem', verticalAlign: 'middle' }} />
                </Tooltip>
              )}
            </Stack>
          )
        },
      },
      ...(enableEditDriverGsUser
        ? [
            {
              field: 'gsUserId',
              headerName: 'GreenSpark User',
              flex: 1.5,
              editable: false,
              sortable: false,
              renderCell: ({ row }) => {
                // Get all currently assigned GS user IDs except for the current driver
                const assignedGsUserIds = driversData
                  ?.filter(driver => driver.gsUserId && driver.id !== row.id)
                  .map(driver => driver.gsUserId)

                // Filter out already assigned users and format the remaining ones
                const formattedGsUsersData = gsUsersResponse?.Items?.filter(
                  user => !assignedGsUserIds?.includes(user.id) || user.id === row.gsUserId,
                )?.map(user => ({
                  id: user.id,
                  name: (
                    <Stack spacing={0}>
                      <Typography variant="body2" sx={{ lineHeight: 1 }}>
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        {user.email}
                      </Typography>
                    </Stack>
                  ),
                }))

                return (
                  <Select
                    name={`drivers.${row.index}.gsUserId`}
                    size="small"
                    control={control}
                    defaultValue={row.gsUserId}
                    selectProps={{
                      onChange: event => {
                        setValue(`drivers.${row.index}.gsUserId`, event.target.value, { shouldDirty: true })
                        setValue(`drivers.${row.index}.edited`, true)
                      },
                      ...(driversData[row.index].gsUserId && {
                        IconComponent: () => null,
                        endAdornment: (
                          <IconButton
                            onClick={() => {
                              setValue(`drivers.${row.index}.gsUserId`, '', { shouldDirty: true })
                              setValue(`drivers.${row.index}.edited`, true)
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        ),
                      }),
                    }}
                    items={formattedGsUsersData || []}
                  />
                )
              },
            },
          ]
        : []),
      {
        field: 'truckId',
        headerName: 'Assigned Truck',
        flex: 1.5,
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Select
              name={`drivers.${row.index}.truckId`}
              size="small"
              control={control}
              defaultValue={row.truckId || ''}
              selectProps={{
                onChange: event => {
                  setValue(`drivers.${row.index}.truckId`, event.target.value, { shouldDirty: true })
                  setValue(`drivers.${row.index}.edited`, true)
                },
                ...(driversData[row.index].truckId && {
                  IconComponent: () => null,
                  endAdornment: (
                    <IconButton
                      onClick={() => {
                        setValue(`drivers.${row.index}.truckId`, '', { shouldDirty: true })
                        setValue(`drivers.${row.index}.edited`, true)
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }),
              }}
              items={formattedTrucksData || []}
            />
          )
        },
      },
      {
        field: 'trailerId',
        headerName: 'Assigned Trailer',
        flex: 1.5,
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Select
              name={`drivers.${row.index}.trailerId`}
              size="small"
              control={control}
              defaultValue={row.trailerId || ''}
              selectProps={{
                onChange: event => {
                  setValue(`drivers.${row.index}.trailerId`, event.target.value, { shouldDirty: true })
                  setValue(`drivers.${row.index}.edited`, true)
                },
                ...(driversData[row.index].trailerId && {
                  IconComponent: () => null,
                  endAdornment: (
                    <IconButton
                      onClick={() => {
                        setValue(`drivers.${row.index}.trailerId`, '', { shouldDirty: true })
                        setValue(`drivers.${row.index}.edited`, true)
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }),
              }}
              items={formattedTrailersData || []}
            />
          )
        },
      },
      {
        field: 'archive',
        headerName: '',
        flex: 1,
        align: 'center',
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <IconButton
              onClick={() => {
                setConfirmArchiveModal(true)
                setDriverIdToArchive(row.id)
              }}
            >
              <DeleteIcon />
            </IconButton>
          )
        },
      },
    ]

    return columns
  }, [driversData, trucksData, trailersData])

  return (
    <GridStyling>
      <ConfirmDialog
        open={confirmArchiveModal}
        title="Archive Driver"
        question="Are you sure you'd like to archive this driver?"
        description={
          <>
            This action cannot be undone.
            <br />
            If you'd like to temporary deactivate this driver, use the 'Active' checkbox instead.
          </>
        }
        confirmLabel="YES, ARCHIVE DRIVER"
        cancelLabel="CANCEL"
        onClose={() => {
          setConfirmArchiveModal(false)
        }}
        onSubmit={() => {
          onArchiveDriver(driverIdToArchive)
        }}
      />
      <BasicDataGridV2
        dataTestId="manage-drivers-grid"
        rows={driversData}
        columns={driversColumns}
        loading={isLoading}
        disableColumnMenu
        pagination={false}
        components={{
          LoadingOverlay: () => null,
        }}
      />
    </GridStyling>
  )
}

export default ManageDriversProperties
